import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '@/lib/api-client';
import { configureLocalDatabase } from '@/lib/db';
import { useAuth } from '@/hooks/use-auth';

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface TenantContextState {
  /** List of all tenants (only populated for super-admins). */
  tenants: TenantInfo[];
  /** The currently active tenant ID (override or default from auth). */
  activeTenantId: string;
  /** The active tenant's display name. */
  activeTenantName: string;
  /** Whether we're viewing a tenant other than our home tenant. */
  isOverriding: boolean;
  /** Switch to a different tenant (super-admin only). */
  switchTenant: (tenantId: string) => void;
  /** Reset to the user's home tenant. */
  resetTenant: () => void;
  /** Loading state for tenant list. */
  loading: boolean;
}

const TenantContext = createContext<TenantContextState | undefined>(undefined);

const STORAGE_KEY = 'structureai_tenant_override';

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isSuperAdmin } = useAuth();
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  // Read override from localStorage (set before page reload by switchTenant/resetTenant)
  const [overrideTenantId] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || '';
  });
  const [loading, setLoading] = useState(false);

  const homeTenantId = user?.tenantId || '';

  // Load tenant list for super-admins
  useEffect(() => {
    if (!isSuperAdmin) {
      setTenants([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await apiClient.get<TenantInfo[]>('/platform/tenants');
        if (!cancelled) {
          setTenants(data);
        }
      } catch (err) {
        console.error('[Tenant] Failed to load tenants:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const activeTenantId = (isSuperAdmin && overrideTenantId) ? overrideTenantId : homeTenantId;
  const isOverriding = isSuperAdmin && !!overrideTenantId && overrideTenantId !== homeTenantId;

  configureLocalDatabase(
    user?.uid && activeTenantId
      ? { userId: user.uid, tenantId: activeTenantId }
      : null,
  );

  const activeTenantName = useMemo(() => {
    const match = tenants.find((t) => t.id === activeTenantId);
    return match?.name || activeTenantId;
  }, [tenants, activeTenantId]);

  const switchTenant = useCallback((tenantId: string) => {
    if (tenantId && tenantId !== homeTenantId) {
      localStorage.setItem(STORAGE_KEY, tenantId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Reload the page so all data hooks re-fetch from the new tenant context
    window.location.reload();
  }, [homeTenantId]);

  const resetTenant = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  }, []);

  // Expose the active tenant ID globally so apiClient can read it
  useEffect(() => {
    if (isOverriding) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__tenantOverride = activeTenantId;
    } else {
      delete // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__tenantOverride;
    }
  }, [isOverriding, activeTenantId]);

  const value = useMemo<TenantContextState>(
    () => ({
      tenants,
      activeTenantId,
      activeTenantName,
      isOverriding,
      switchTenant,
      resetTenant,
      loading,
    }),
    [tenants, activeTenantId, activeTenantName, isOverriding, switchTenant, resetTenant, loading],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextState {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
